import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AxiosInstance } from "axios";
import moment from "moment";
import { BootstrapResponse, BootstrapResponseEvent } from "../models/api";
import { MatchTimes, Matchup, MatchupTeamEntry, PlayerStats, TeamFixture } from "../models";
import { entryIdsFor, isInsideMatchWindow, matchupsFromResponse, scoreMatchups, summariseFixtures } from "../scoring";
import { fetchBootstrap, fetchEntryPicks, fetchFixtures, fetchLeagueMatches, fetchLiveWeek } from "./api";
import { currentEvent, eventById, playersFrom, teamsFrom } from "./bootstrap";

/** Seconds between live refreshes while a match is in its window. */
export const REFRESH_INTERVAL_SECONDS = 60;

export type GameweekData = {
	/** The gameweek being shown. */
	week: BootstrapResponseEvent | null;
	/** The latest gameweek FPL considers current. */
	maxWeekId: number | null;
	matchups: Matchup[];
	loading: boolean;
	loadingMessage: string;
	error: string | null;
	/** True while the shown gameweek is still open. */
	gameweekLive: boolean;
	/** True while at least one fixture is inside its live window. */
	matchesLive: boolean;
	secondsToNextFetch: number;
	/** Premier League fixtures currently in play. */
	matchesInPlay: number;
	gotoWeek: (weekId: number) => void;
};

const emptySummary = { teamFixtures: [] as TeamFixture[], matchTimes: [] as MatchTimes[] };

export const useGameweek = (api: AxiosInstance, leagueId: number): GameweekData => {
	const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
	const [weekId, setWeekId] = useState<number | null>(null);

	const [playerStats, setPlayerStats] = useState<PlayerStats[]>([]);
	const [fixtureSummary, setFixtureSummary] = useState(emptySummary);
	const [matchups, setMatchups] = useState<Matchup[] | null>(null);
	const [entries, setEntries] = useState<MatchupTeamEntry[]>([]);

	const [loadingMessage, setLoadingMessage] = useState("Fetching Gameweek");
	const [error, setError] = useState<string | null>(null);

	const [gameweekLive, setGameweekLive] = useState(false);
	const [matchesLive, setMatchesLive] = useState(false);
	const [secondsToNextFetch, setSecondsToNextFetch] = useState(REFRESH_INTERVAL_SECONDS);
	const nextFetchTime = useRef<moment.Moment | null>(null);

	const players = useMemo(() => (bootstrap ? playersFrom(bootstrap) : []), [bootstrap]);
	const teams = useMemo(() => (bootstrap ? teamsFrom(bootstrap) : []), [bootstrap]);
	const week = useMemo(() => (bootstrap && weekId != null ? eventById(bootstrap, weekId) ?? null : null), [bootstrap, weekId]);
	const maxWeekId = useMemo(() => (bootstrap ? currentEvent(bootstrap)?.id ?? null : null), [bootstrap]);

	// Bootstrap once, then land on the current gameweek
	useEffect(() => {
		let cancelled = false;
		fetchBootstrap(api)
			.then((data) => {
				if (cancelled) return;
				setBootstrap(data);
				setWeekId(currentEvent(data)?.id ?? null);
			})
			.catch(() => {
				if (cancelled) return;
				setError("Failed to load data. Please refresh.");
			});
		return () => {
			cancelled = true;
		};
	}, [api]);

	const refreshLive = useCallback(
		(targetWeekId: number) => {
			nextFetchTime.current = moment().add(REFRESH_INTERVAL_SECONDS, "seconds");

			fetchLiveWeek(api, targetWeekId)
				.then((live) => setPlayerStats(live.elements.map((p) => ({ id: p.id, points: p.stats.total_points, data: p.explain }))))
				.catch(() => setError("Failed to fetch live scores."));

			fetchFixtures(api, targetWeekId)
				.then((fixtures) => setFixtureSummary(summariseFixtures(fixtures, teams)))
				.catch(() => setError("Failed to fetch fixture data."));
		},
		[api, teams],
	);
	const refreshLiveRef = useRef(refreshLive);
	refreshLiveRef.current = refreshLive;

	// Load everything for the selected gameweek
	useEffect(() => {
		if (bootstrap == null || weekId == null) {
			return;
		}

		let cancelled = false;
		setPlayerStats([]);
		setFixtureSummary(emptySummary);
		setMatchups(null);
		setEntries([]);
		setError(null);

		refreshLiveRef.current(weekId);

		setLoadingMessage("Fetching League Fixtures");
		fetchLeagueMatches(api, leagueId, weekId)
			.then((response) => {
				if (cancelled) return;
				const skeletons = matchupsFromResponse(response.results);
				setMatchups(skeletons);

				const entryIds = entryIdsFor(skeletons);
				setLoadingMessage("Fetching Team Selections");
				return Promise.all(entryIds.map((id) => fetchEntryPicks(api, id, weekId)))
					.then((picks) => {
						if (cancelled) return;
						setEntries(
							picks.map((p, index) => ({
								id: entryIds[index],
								chip: p.active_chip,
								picks: p.picks,
								basePoints: p.entry_history.event_transfers_cost * -1,
							})),
						);
					})
					.catch(() => {
						if (!cancelled) setError("Failed to fetch team selections.");
					});
			})
			.catch(() => {
				if (!cancelled) setError("Failed to load league fixtures. Please refresh.");
			});

		return () => {
			cancelled = true;
		};
	}, [api, leagueId, bootstrap, weekId]);

	// Poll while the gameweek is open and a match is inside its window
	useEffect(() => {
		if (week == null || week.finished) {
			setGameweekLive(false);
			setMatchesLive(false);
			return;
		}

		setGameweekLive(true);
		const timer = setInterval(() => {
			if (isInsideMatchWindow(fixtureSummary.matchTimes, moment().unix())) {
				setMatchesLive(true);
				const secondDelta = nextFetchTime.current ? nextFetchTime.current.diff(moment(), "seconds") : 0;
				if (secondDelta > 0) {
					setSecondsToNextFetch(secondDelta);
				} else if (weekId != null) {
					refreshLiveRef.current(weekId);
				}
			} else {
				setMatchesLive(false);
			}
		}, 1000);

		return () => {
			clearInterval(timer);
			setGameweekLive(false);
			setMatchesLive(false);
		};
	}, [week, weekId, fixtureSummary.matchTimes]);

	const scored = useMemo(() => scoreMatchups(matchups ?? [], entries, players, playerStats, fixtureSummary.teamFixtures), [matchups, entries, players, playerStats, fixtureSummary.teamFixtures]);
	const noMatchups = matchups != null && matchups.length === 0;
	const loading = error == null && scored == null && !noMatchups;

	const matchesInPlay = useMemo(() => new Set(fixtureSummary.teamFixtures.filter((f) => f.started && !f.finished).map((f) => f.fixtureId)).size, [fixtureSummary.teamFixtures]);

	const gotoWeek = useCallback((id: number) => setWeekId(id), []);

	return {
		week,
		maxWeekId,
		matchups: scored ?? [],
		loading,
		loadingMessage,
		error,
		gameweekLive,
		matchesLive,
		secondsToNextFetch,
		matchesInPlay,
		gotoWeek,
	};
};
